<script setup lang="ts">
    import { computed, onBeforeMount, ref } from 'vue';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import {
        ICreateWidget,
        IKpiSubCategory,
        VisualizationTypes,
    } from '@/js/services/api/widget/types';
    import AutoComplete from '../autoComplete/AutoComplete.vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import { useWidgetFormStore } from '@/js/stores/widgetForm';
    import { Option } from '../autoComplete/types';
    import InputLabel from '../InputLabel.vue';
    import TextareaInput from '../TextareaInput.vue';
    import FormGroup from '../FormGroup.vue';

    const widgetFormStore = useWidgetFormStore();

    const kpiCategoryOptions = computed(() =>
        widgetFormStore.kpi?.map((kpi) => ({
            name: kpi.translationKey ? __untypedExpression(kpi.translationKey) : kpi.name,
            value: kpi.id,
        }))
    );

    const graphsOptions = ref<Option[]>();
    const kpiOptions = ref<Option[]>();

    // v-model refs for text input — gets cleared to '' by AutoComplete after selection
    const kpiCategoryInputValue = ref<string | Option | null>(null);
    const kpiInputValue = ref<string | Option | null>(null);
    const graphInputValue = ref<string | Option | null>(null);

    // Separate refs for tracking what's actually selected
    const selectedKpiCategory = ref<Option | null>(null);
    const selectedKpiOption = ref<Option | null>(null);
    const selectedGraph = ref<Option | null>(null);

    const visualizationGraphs = ref<VisualizationTypes[]>([]);

    onBeforeMount(() => {
        if ((widgetFormStore.widget as ICreateWidget<'Graph'>).kpiVisualizationTypeId) {
            const foundKpi = widgetFormStore.kpi
                ?.flatMap((k) => k.variants)
                .find((x) =>
                    x.visualizationTypes.find(
                        (type) =>
                            type.id ===
                            (widgetFormStore.widget as ICreateWidget<'Graph'>)
                                .kpiVisualizationTypeId
                    )
                );

            const foundKpiCategory = widgetFormStore.kpi?.find((k) =>
                k.variants.find((v) => v.id === foundKpi?.id)
            );

            const visualization = foundKpi?.visualizationTypes.find(
                (type) =>
                    type.id ===
                    (widgetFormStore.widget as ICreateWidget<'Graph'>).kpiVisualizationTypeId
            );

            if (foundKpi && foundKpiCategory && visualization) {
                const kpiOption = {
                    value: foundKpi.id,
                    name: foundKpi.translationKey
                        ? __untypedExpression(foundKpi.translationKey)
                        : foundKpi.description,
                };
                const categoryOption = {
                    value: foundKpiCategory.id,
                    name: foundKpiCategory.translationKey
                        ? __untypedExpression(foundKpiCategory.translationKey)
                        : foundKpiCategory.name,
                };
                const graphOption = {
                    value: visualization.id ?? '',
                    name: visualization.translationKey
                        ? __untypedExpression(visualization.translationKey)
                        : visualization.displayName,
                };

                selectedKpiCategory.value = categoryOption;
                selectedKpiOption.value = kpiOption;
                selectedGraph.value = graphOption;

                // Setting to Option objects triggers AutoComplete's watch to show selected display
                kpiCategoryInputValue.value = categoryOption;
                kpiInputValue.value = kpiOption;
                graphInputValue.value = graphOption;

                kpiOptions.value = setKPIOptions(foundKpiCategory.variants);
                visualizationGraphs.value = foundKpi.visualizationTypes;
                const baseGraphOptions = foundKpi.visualizationTypes.map((t) => ({
                    name: t.translationKey ? __untypedExpression(t.translationKey) : t.displayName,
                    value: t.id,
                    type: t.type,
                }));
                graphsOptions.value = baseGraphOptions.map(({ name, value }) => ({ name, value }));
            }
        }
    });

    const isKPICategorySelected = computed(() => !!selectedKpiCategory.value);
    const isKPISelected = computed(() => !!selectedKpiOption.value);
    const isGraphSelected = computed(() => !!selectedGraph.value);

    const resetForm = () => {
        if ((widgetFormStore.widget as ICreateWidget<'Graph'>).kpiVisualizationTypeId) {
            (widgetFormStore.widget as ICreateWidget<'Graph'>).kpiVisualizationTypeId = '';
        }
        if ((widgetFormStore.widget as ICreateWidget<'Graph'>).graphType) {
            (widgetFormStore.widget as ICreateWidget<'Graph'>).graphType = undefined;
        }
    };

    const setKPIOptions = (variants: IKpiSubCategory[]) =>
        variants.flatMap((k) => ({
            value: k.id,
            name: k.translationKey ? __untypedExpression(k.translationKey) : k.description,
        }));

    const updateSelectedKPICategory = (value: Option) => {
        if (value.value === selectedKpiCategory.value?.value) {
            return;
        }
        selectedKpiCategory.value = value;
        selectedKpiOption.value = null;
        selectedGraph.value = null;
        kpiInputValue.value = null;
        graphInputValue.value = null;
        resetForm();

        const KPICategory = widgetFormStore.kpi?.find((k) => k.id === value.value);

        if (!KPICategory) {
            return;
        }

        kpiOptions.value = setKPIOptions(KPICategory.variants);

        if (kpiOptions.value && kpiOptions.value.length === 1) {
            kpiInputValue.value = kpiOptions.value[0];
            updateSelectedKPI(kpiOptions.value[0]);
        }
    };

    const updateSelectedKPI = (newKPI: Option) => {
        // Re-selecting the same option must not reset the selected graph
        if (newKPI.value === selectedKpiOption.value?.value) {
            return;
        }
        selectedKpiOption.value = newKPI;
        selectedGraph.value = null;
        graphInputValue.value = null;
        resetForm();

        const KPI = widgetFormStore.kpi
            ?.find((k) => k.id === selectedKpiCategory.value?.value)
            ?.variants.find((v) => v.id === newKPI.value);

        if (!KPI) {
            return;
        }

        visualizationGraphs.value = KPI.visualizationTypes;
        const baseGraphOptions = KPI.visualizationTypes.map((t) => ({
            name: t.translationKey ? __untypedExpression(t.translationKey) : t.displayName,
            value: t.id,
            type: t.type,
        }));

        graphsOptions.value = baseGraphOptions.map(({ name, value }) => ({ name, value }));
        if (graphsOptions.value && graphsOptions.value.length === 1) {
            graphInputValue.value = graphsOptions.value[0];
            updateSelectedGraph(graphsOptions.value[0]);
        }
    };

    const updateSelectedGraph = (option: Option) => {
        const visualizationType = visualizationGraphs.value.find(
            (g) => g.id === option.value
        )?.type;
        if (!visualizationType) {
            return;
        }
        selectedGraph.value = option;
        widgetFormStore.updateWidget('kpiVisualizationTypeId', option.value);
        widgetFormStore.updateWidget('graphType', visualizationType);
    };

    const clearSelectedKpiCategory = () => {
        selectedKpiCategory.value = null;
        selectedKpiOption.value = null;
        selectedGraph.value = null;
        kpiInputValue.value = null;
        graphInputValue.value = null;
        kpiOptions.value = undefined;
        graphsOptions.value = undefined;
        resetForm();
    };

    const clearSelectedKpi = () => {
        selectedKpiOption.value = null;
        selectedGraph.value = null;
        graphInputValue.value = null;
        graphsOptions.value = undefined;
        resetForm();
    };

    const clearSelectedGraph = () => {
        selectedGraph.value = null;
        resetForm();
    };
</script>

<template>
    <FormGroup>
        <div v-if="kpiCategoryOptions?.length" :class="{ 'ac-required': !isKPICategorySelected }">
            <AutoComplete
                v-model="kpiCategoryInputValue"
                :label="__('widget-form-category-kpi-title')"
                :input-id="__('widget-form-category-kpi-title')"
                :options="kpiCategoryOptions"
                @select="updateSelectedKPICategory"
                @clear="clearSelectedKpiCategory"
            ></AutoComplete>
        </div>
        <div
            v-if="kpiOptions?.length"
            :class="{ 'ac-required': isKPICategorySelected && !isKPISelected }"
        >
            <AutoComplete
                v-model="kpiInputValue"
                :label="__('widget-form-kpi-title')"
                :input-id="__('widget-form-kpi-title')"
                :options="kpiOptions"
                @select="updateSelectedKPI"
                @clear="clearSelectedKpi"
            ></AutoComplete>
        </div>
        <div
            v-if="graphsOptions?.length"
            :class="{ 'ac-required': isKPISelected && !isGraphSelected }"
        >
            <AutoComplete
                v-model="graphInputValue"
                :label="__('widget-form-graph-type-title')"
                :input-id="__('widget-form-graph-type-title')"
                :options="graphsOptions"
                @select="updateSelectedGraph"
                @clear="clearSelectedGraph"
            ></AutoComplete>
        </div>
        <p
            v-if="!(isKPICategorySelected && isKPISelected && isGraphSelected)"
            class="text-body -mt-3"
        >
            <span class="text-error" aria-hidden="true">*</span>
            {{ __('widget-form-required-field') }}
        </p>

        <div>
            <InputLabel for="widget-form-name-input-title">
                {{ __('widget-form-name-input-title') }}
            </InputLabel>
            <TextInput
                id="widget-form-name-input-title"
                v-model="widgetFormStore.widget.title"
                :placeholder="__('widget-form-name-input-placeholder')"
                :width="TextInputWidth.Full"
            />
        </div>
        <div>
            <InputLabel for="widget-form-description-input-title">
                {{ __('widget-form-description-input-title') }}
            </InputLabel>
            <TextareaInput
                id="widget-form-description-input-title"
                v-model="widgetFormStore.widget.description"
                :placeholder="__('widget-form-description-input-placeholder')"
                :rows="3"
            />
        </div>
    </FormGroup>
</template>
